import { useCallback } from 'react';
import _ from 'underscore';

export const useIncludeOrExcludeAspectRatio = ({
  imageList, requiredAspectRatios, previewImagesSharedState, setPreviewImagesSharedState, onUpdateImageList, isControlledByInlineEditingImageId,
}) => {
  const getImageIndexFromId = useCallback((id) => {
    const index = _.findIndex(imageList, placement => _.first(placement).id === id && !_.first(placement).isLogo && !_.first(placement).isPortrait);
    return index >= 0 ? index : null;
  }, [imageList]);

  const getPreviewImageIndex = useCallback((idRequestedByImageExperience) => {
    if (imageList && imageList.length > 0) {
      let index = getImageIndexFromId(idRequestedByImageExperience);

      if (!index) {
        const firstImageIndex = _.findIndex(imageList, placement => !_.first(placement).isLogo);
        index = firstImageIndex >= 0 ? firstImageIndex : null;
      }

      return index;
    }
    return null;
  }, [getImageIndexFromId, imageList]);

  const { previewImageId, inlineEditingImageId } = previewImagesSharedState;

  let previewImageIndex = null;
  if (imageList && imageList.length > 0) {
    previewImageIndex = getPreviewImageIndex(isControlledByInlineEditingImageId ? inlineEditingImageId : previewImageId);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const canExcludeAspectRatio = useCallback((typeToCheck) => {
    const minCount = requiredAspectRatios[typeToCheck];
    if (!minCount) {
      return true;
    }

    let typeCount = 0;
    _.each(imageList, imageArray =>
      _.each(imageArray, (imageObj) => {
        if (imageObj.isIncluded && typeToCheck === imageObj.type) {
          typeCount += 1;
        }
      }));

    return (typeCount - 1) >= minCount;
  }, [imageList, requiredAspectRatios]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getIncludedTypeList = useCallback(() => {
    if (previewImageIndex === null || !_.isArray(imageList)) {
      return null;
    }

    const typeList = [];
    _.each(imageList[previewImageIndex], (imageObj) => {
      if (imageObj.isIncluded) {
        typeList.push(imageObj.type);
      }
    });
    return typeList;
  }, [imageList, previewImageIndex]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onToggleAspectRatio = useCallback((type, isIncluded) => {
    if (previewImageIndex === null) {
      return;
    }

    const newImageList = [...imageList];
    const targetPlacement = _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type);

    if (targetPlacement) {
      targetPlacement.isIncluded = isIncluded;
    }

    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true, editingPlacementType: '' });
    onUpdateImageList(newImageList);
  }, [imageList, onUpdateImageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState]);

  const onAddAspectRatio = useCallback((type) => {
    if (previewImageIndex === null) {
      return;
    }

    const newImageList = [...imageList];
    _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type).isIncluded = true;
    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true, editingPlacementType: '' });
    onUpdateImageList(newImageList);
  }, [imageList, onUpdateImageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState]);

  const onRemoveAspectRatio = useCallback((type) => {
    if (previewImageIndex === null) {
      return;
    }

    const newImageList = [...imageList];
    _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type).isIncluded = false;
    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true, editingPlacementType: '' });
    onUpdateImageList(newImageList);
  }, [imageList, onUpdateImageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState]);

  return {
    previewImageIndex,
    canExcludeAspectRatio,
    getIncludedTypeList,
    onToggleAspectRatio,
    onAddAspectRatio,
    onRemoveAspectRatio,
  };
};
